import { isGroup, type IActivityCategory } from "@medoma/models/activities";
import {
  type IInvoiceStatus,
  type IInvoicingActivityOccurrenceOrGroup,
  type IInvoicingCode,
} from "@medoma/models/invoicing";
import { getPatientFullName } from "@medoma/models/patients";
import type { IMedicalCompetence } from "@medoma/models/shifts";

const getDataFromInvoicingOccurrencesAndGroups = (
  invoicingOccurrencesAndGroups: IInvoicingActivityOccurrenceOrGroup[],
) => {
  return invoicingOccurrencesAndGroups.map((occurrenceOrGroup) => {
    if (isGroup(occurrenceOrGroup)) {
      const group = occurrenceOrGroup;
      const uniqueCompetencesMap = new Map<string, IMedicalCompetence>();
      const uniqueProductCodesMap = new Map<string, IInvoicingCode>();
      const uniqueProcedureCodesMap = new Map<string, IInvoicingCode>();
      const occurrenceTitles: {
        occurrenceId: string;
        activityId: string;
        title: string;
        status: IInvoiceStatus;
        finishedAt: Date | undefined | null;
        activityType: IActivityCategory;
        procedureCodes: IInvoicingCode[];
        productCodes: IInvoicingCode[];
      }[] = [];

      for (const occurrence of group.occurrences) {
        for (const competence of occurrence.requiredCompetences) {
          if (!uniqueCompetencesMap.has(competence)) {
            uniqueCompetencesMap.set(competence, competence);
          }
        }
        for (const productCode of occurrence.productCodes) {
          if (!uniqueProductCodesMap.has(productCode.code)) {
            uniqueProductCodesMap.set(productCode.code, productCode);
          }
        }
        for (const procedureCode of occurrence.procedureCodes) {
          if (!uniqueProcedureCodesMap.has(procedureCode.code)) {
            uniqueProcedureCodesMap.set(procedureCode.code, procedureCode);
          }
        }
        occurrenceTitles.push({
          occurrenceId: occurrence.occurrenceId,
          activityId: occurrence.activityId,
          title: occurrence.title,
          status: occurrence.status,
          finishedAt: occurrence.finishedAt,
          activityType: occurrence.activityType,
          procedureCodes: occurrence.procedureCodes,
          productCodes: occurrence.productCodes,
        });
      }

      const uniqueCompetences = Array.from(uniqueCompetencesMap.values());
      const uniqueProductCodes = Array.from(uniqueProductCodesMap.values());
      const uniqueProcedureCodes = Array.from(uniqueProcedureCodesMap.values());

      return {
        patient: {
          id: group.patient.id,
          name: getPatientFullName(group.patient.name),
          paymentExempt: group.patient.paymentExempt,
        },
        personalIdentityNumber: {
          number: group.patient.personalIdentityNumber,
          type: group.patient.personalIdentityNumberType,
        },
        patientType: group.patient.patientType,
        competences: uniqueCompetences,
        finishedAt: group.occurrences.find(({ finishedAt }) => finishedAt)
          ?.finishedAt,
        title: occurrenceTitles,
        diagnosisCode: group.patient.diagnosis?.code,
        procedureCodes: uniqueProcedureCodes,
        productCodes: uniqueProductCodes,
        // If there is an error, show the first
        comment: group.occurrences.find(({ error }) => error)?.error,
        actions: { groupId: group.groupId },
      };
    } else {
      const occurrence = occurrenceOrGroup;
      return {
        patient: {
          id: occurrence.patient.id,
          name: getPatientFullName(occurrence.patient.name),
          paymentExempt: occurrence.patient.paymentExempt,
        },
        personalIdentityNumber: {
          number: occurrence.patient.personalIdentityNumber,
          type: occurrence.patient.personalIdentityNumberType,
        },
        patientType: occurrence.patient.patientType,
        competences: occurrence.requiredCompetences,
        finishedAt: occurrence.finishedAt,
        title: [
          {
            occurrenceId: occurrence.occurrenceId,
            activityId: occurrence.activityId,
            title: occurrence.title,
            finishedAt: occurrence.finishedAt,
            status: occurrence.status,
            activityType: occurrence.activityType,
            procedureCodes: occurrence.procedureCodes,
            productCodes: occurrence.productCodes,
          },
        ],
        diagnosisCode: occurrence.patient.diagnosis?.code,
        procedureCodes: occurrence.procedureCodes,
        productCodes: occurrence.productCodes,
        comment: occurrence.error,
        actions: {
          activityId: occurrence.activityId,
          occurrenceId: occurrence.occurrenceId,
        },
      };
    }
  });
};

export { getDataFromInvoicingOccurrencesAndGroups };
